// src/pages/UserAdmin/AccountSettings.js — /config-conta
//
// PR 1 do plano de "Config da Conta":
//   ✓ Aba Perfil: avatar upload + nome + email (read-only na v1) + telefone
//   ✓ Aba Preferências: idioma + toggles de notificação
//   ⏳ Aba Segurança: placeholder (chega no PR 2)
//   ⏳ Aba Plano e faturamento: placeholder (chega no PR 3)
//
// Após salvar/trocar avatar, dispara `window.dispatchEvent(new Event('user-updated'))`
// pro AppSidebar re-ler o localStorage e atualizar o avatar do rodapé sem
// exigir reload da página.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, Divider, Stack, CircularProgress, Chip, IconButton,
  Tooltip, Tabs, Tab, Container, Avatar, Switch, InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FiUser, FiShield, FiSliders, FiCreditCard,
  FiCamera, FiTrash2, FiSave, FiLock, FiInfo,
  FiEye, FiEyeOff, FiCheck,
} from 'react-icons/fi';
import AppSidebar from '../../components/AppSidebar';
import TopBar from '../../components/TopBar';
import api from '../../services/Api';

const SYNE = "'Syne', system-ui, sans-serif";

const ROLE_LABEL = {
  ADMIN: 'Admin',
  SUPERVISOR: 'Supervisor',
  AGENT: 'Agente',
};

const LANGUAGES = [
  { code: 'pt_BR', label: 'Português (Brasil)' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'es_ES', label: 'Español' },
];

// Resolve URL absoluta para preview de imagens hospedadas no backend.
function resolveMediaSrc(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br'}${url}`;
}

// Persiste no localStorage + dispara evento pra outros componentes
// (ex: AppSidebar) atualizarem em tempo real.
function syncUserToLocalStorage(payload) {
  try {
    const current = JSON.parse(localStorage.getItem('user') || '{}');
    const merged = { ...current, ...payload };
    localStorage.setItem('user', JSON.stringify(merged));
    window.dispatchEvent(new Event('user-updated'));
  } catch {
    /* localStorage cheio ou inválido — silencioso, próximo login corrige */
  }
}

export default function AccountSettings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });

  // ── Perfil ─────────────────────────────────────────────────────────────
  const [me, setMe] = useState(null); // dados atuais do backend
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const avatarInputRef = useRef(null);

  // ── Preferências ───────────────────────────────────────────────────────
  const [language, setLanguage] = useState('pt_BR');
  const [notifyMsgs, setNotifyMsgs] = useState(true);
  const [notifyHealth, setNotifyHealth] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  // ── Segurança: alterar senha ───────────────────────────────────────────
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdShowCurrent, setPwdShowCurrent] = useState(false);
  const [pwdShowNew, setPwdShowNew] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState(false);

  const pwdNewOk = pwdNew.length >= 8;
  const pwdConfirmOk = pwdConfirm.length > 0 && pwdConfirm === pwdNew;
  const pwdFormValid = pwdCurrent && pwdNewOk && pwdConfirmOk && pwdCurrent !== pwdNew;

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/users/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setMe(res.data);
        setName(res.data.name || '');
        setPhone(res.data.phone_number || '');
        setLanguage(res.data.language || 'pt_BR');
        setNotifyMsgs(!!res.data.notify_new_messages);
        setNotifyHealth(!!res.data.notify_health_alerts);
      } catch (err) {
        setMsg({ type: 'error', text: err.response?.data?.detail || 'Erro ao carregar seus dados.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  const profileDirty = useMemo(() => (
    !!me && (
      (name || '') !== (me.name || '') ||
      (phone || '') !== (me.phone_number || '')
    )
  ), [name, phone, me]);

  const prefsDirty = useMemo(() => (
    !!me && (
      language !== (me.language || 'pt_BR') ||
      notifyMsgs !== !!me.notify_new_messages ||
      notifyHealth !== !!me.notify_health_alerts
    )
  ), [language, notifyMsgs, notifyHealth, me]);

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      setMsg({ type: 'error', text: 'Nome não pode ficar vazio.' });
      return;
    }
    setSavingProfile(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await api.patch('/users/me',
        { name: name.trim(), phone_number: phone.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMe(res.data);
      syncUserToLocalStorage({
        name: res.data.name,
        profile_photo_url: res.data.profile_photo_url,
      });
      setMsg({ type: 'success', text: 'Perfil atualizado.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Erro ao salvar perfil.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await api.patch('/users/me', {
        language,
        notify_new_messages: notifyMsgs,
        notify_health_alerts: notifyHealth,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMe(res.data);
      setMsg({ type: 'success', text: 'Preferências salvas.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Erro ao salvar preferências.' });
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleChangePassword = async () => {
    setPwdError('');
    setPwdSuccess(false);
    if (!pwdFormValid) {
      // Fallback defensivo — o botão já está disabled, mas garantimos.
      setPwdError('Verifique os campos antes de salvar.');
      return;
    }
    setPwdSaving(true);
    try {
      const res = await api.post('/users/me/password', {
        current_password: pwdCurrent,
        new_password: pwdNew,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Out/26: trocar a senha desconecta todos os aparelhos, inclusive este
      // login; a resposta traz um token novo pra este continuar.
      if (res.data?.access_token) localStorage.setItem('token', res.data.access_token);
      setPwdSuccess(true);
      setPwdCurrent(''); setPwdNew(''); setPwdConfirm('');
    } catch (err) {
      setPwdError(err.response?.data?.detail || 'Erro ao alterar senha.');
    } finally {
      setPwdSaving(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    setMsg({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/users/me/avatar', formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setMe(res.data);
      syncUserToLocalStorage({ profile_photo_url: res.data.profile_photo_url });
      setMsg({ type: 'success', text: 'Foto atualizada.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Erro ao enviar foto.' });
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    setUploadingAvatar(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await api.delete('/users/me/avatar', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMe(res.data);
      syncUserToLocalStorage({ profile_photo_url: null });
      setMsg({ type: 'success', text: 'Foto removida.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Erro ao remover foto.' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const userForSidebar = me ? {
    ...JSON.parse(localStorage.getItem('user') || '{}'),
    name: me.name,
    role: me.role,
    profile_photo_url: me.profile_photo_url,
  } : JSON.parse(localStorage.getItem('user') || '{}');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <AppSidebar user={userForSidebar} />
        <Box sx={{
          flexGrow: 1, ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CircularProgress sx={{ color: '#25d366' }} />
        </Box>
      </Box>
    );
  }

  const isSupervisor = me?.role === 'SUPERVISOR';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppSidebar user={userForSidebar} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
          transition: 'margin-left 0.25s ease',
          display: 'flex', flexDirection: 'column', minHeight: '100vh',
        }}
      >
        <TopBar crumbBase="Conta" crumbCur="Configurações" />

        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 3, md: 6 } }}>
          <Container maxWidth="lg" disableGutters>

            {/* Header */}
            <Box sx={{ mb: 4 }}>
              <Typography sx={{
                fontFamily: SYNE, fontWeight: 800, fontSize: '1.65rem',
                letterSpacing: '-0.5px', lineHeight: 1.1, color: '#0f172a',
              }}>
                Configurações da conta
              </Typography>
              <Typography variant="body1" color="#64748b" sx={{ mt: 1 }}>
                Perfil, preferências e segurança da sua conta na Cetto.
              </Typography>
            </Box>

            {msg.text && (
              <Alert
                severity={msg.type || 'info'}
                sx={{ mb: 3, borderRadius: 3 }}
                onClose={() => setMsg({ type: '', text: '' })}
              >
                {msg.text}
              </Alert>
            )}

            {/* Tabs */}
            <Paper elevation={0} sx={{
              borderRadius: 4, border: '1px solid #e2e8f0',
              overflow: 'hidden', bgcolor: '#fff',
            }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  px: { xs: 2, md: 4 },
                  borderBottom: '1px solid #f1f5f9',
                  '& .MuiTabs-indicator': { backgroundColor: '#25d366', height: 3 },
                  '& .MuiTab-root': {
                    textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
                    fontSize: '0.95rem', color: '#64748b', minHeight: 56,
                    '&.Mui-selected': { color: '#0f172a' },
                  },
                }}
              >
                <Tab icon={<FiUser size={16} />} iconPosition="start" label="Perfil" />
                <Tab icon={<FiShield size={16} />} iconPosition="start" label="Segurança" />
                <Tab icon={<FiSliders size={16} />} iconPosition="start" label="Preferências" />
                {isSupervisor && (
                  <Tab icon={<FiCreditCard size={16} />} iconPosition="start" label="Plano e faturamento" />
                )}
              </Tabs>

              {/* ─── PERFIL ─── */}
              {tab === 0 && (
                <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                  <Paper elevation={0} sx={{
                    border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden', mb: 3,
                  }}>
                    <SectionHeader icon={<FiUser size={16} color="#16a34a" />} title="Foto de perfil" />
                    <Box sx={{ p: 3, display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Avatar
                        src={resolveMediaSrc(me?.profile_photo_url)}
                        sx={{
                          width: 88, height: 88,
                          bgcolor: '#f0fdf4', color: '#16a34a',
                          border: '2px solid #bbf7d0',
                          fontFamily: SYNE, fontWeight: 800, fontSize: '2rem',
                        }}
                      >
                        {(me?.name || '?')[0]?.toUpperCase() || '?'}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 220 }}>
                        <Typography sx={{
                          fontFamily: SYNE, fontWeight: 700, color: '#0f172a', mb: 0.5,
                        }}>
                          Sua foto ou logo da empresa
                        </Typography>
                        <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mb: 1.5 }}>
                          Aparece no menu lateral. JPG, PNG ou WEBP até 3 MB.
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            component="label"
                            disabled={uploadingAvatar}
                            startIcon={uploadingAvatar
                              ? <CircularProgress size={14} sx={{ color: '#25d366' }} />
                              : <FiCamera size={14} />}
                            sx={{
                              textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
                              borderColor: '#e2e8f0', color: '#0f172a', borderRadius: 2,
                              '&:hover': { borderColor: '#25d366', bgcolor: '#f0fdf4', color: '#16a34a' },
                            }}
                          >
                            {uploadingAvatar ? 'Enviando...' : (me?.profile_photo_url ? 'Trocar foto' : 'Adicionar foto')}
                            <input
                              ref={avatarInputRef} type="file" hidden
                              accept="image/jpeg,image/png,image/webp"
                              onChange={handleAvatarChange}
                            />
                          </Button>
                          {me?.profile_photo_url && (
                            <Button
                              onClick={handleRemoveAvatar}
                              disabled={uploadingAvatar}
                              startIcon={<FiTrash2 size={14} />}
                              sx={{
                                textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
                                color: '#dc2626', borderRadius: 2,
                                '&:hover': { bgcolor: '#fef2f2' },
                              }}
                            >
                              Remover
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </Paper>

                  <Paper elevation={0} sx={{
                    border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden',
                  }}>
                    <SectionHeader icon={<FiUser size={16} color="#16a34a" />} title="Dados do usuário" />
                    <Box sx={{ p: 3 }}>
                      <Stack spacing={2.5}>
                        <TextField
                          label="Nome de exibição"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          fullWidth size="small"
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            label="Email"
                            value={me?.email || ''}
                            fullWidth size="small"
                            InputProps={{ readOnly: true }}
                            helperText="Para alterar seu email, fale com o suporte."
                          />
                          <TextField
                            label="Cargo"
                            value={ROLE_LABEL[me?.role] || me?.role || '—'}
                            fullWidth size="small"
                            InputProps={{ readOnly: true }}
                            helperText="Definido pelo administrador da empresa."
                          />
                        </Stack>
                        <TextField
                          label="Telefone de contato"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="Ex: (21) 99999-9999"
                          fullWidth size="small"
                          helperText="Usado apenas como contato interno. Não é ligado ao WhatsApp."
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="flex-end" mt={3}>
                        <Button
                          onClick={handleSaveProfile}
                          disabled={!profileDirty || savingProfile}
                          variant="contained"
                          startIcon={savingProfile
                            ? <CircularProgress size={14} color="inherit" />
                            : <FiSave size={14} />}
                          sx={{
                            bgcolor: '#25d366', color: '#0f172a', fontWeight: 800,
                            fontFamily: SYNE, textTransform: 'none', borderRadius: 2,
                            py: 1, px: 3,
                            boxShadow: '0 4px 15px rgba(37,211,102,0.3)',
                            '&:hover': { bgcolor: '#16a34a', color: '#fff' },
                            '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
                          }}
                        >
                          {savingProfile ? 'Salvando...' : 'Salvar alterações'}
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                </Box>
              )}

              {/* ─── SEGURANÇA ─── */}
              {tab === 1 && (
                <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                  <Paper elevation={0} sx={{
                    border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden', mb: 3,
                  }}>
                    <SectionHeader
                      icon={<FiLock size={16} color="#16a34a" />}
                      title="Alterar senha"
                      sub="Escolha uma senha com pelo menos 8 caracteres."
                    />
                    <Box sx={{ p: 3 }}>
                      {pwdError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setPwdError('')}>
                          {pwdError}
                        </Alert>
                      )}
                      {pwdSuccess && (
                        <Alert
                          severity="success"
                          icon={<FiCheck size={16} />}
                          sx={{ mb: 2, borderRadius: 2 }}
                          onClose={() => setPwdSuccess(false)}
                        >
                          Senha atualizada. Os outros aparelhos saíram da conta e vão pedir a senha nova.
                        </Alert>
                      )}
                      <Stack spacing={2.5} sx={{ maxWidth: 460 }}>
                        <TextField
                          label="Senha atual"
                          type={pwdShowCurrent ? 'text' : 'password'}
                          value={pwdCurrent}
                          onChange={(e) => setPwdCurrent(e.target.value)}
                          size="small"
                          fullWidth
                          autoComplete="current-password"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => setPwdShowCurrent(v => !v)}
                                  edge="end"
                                  aria-label={pwdShowCurrent ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                  {pwdShowCurrent ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          label="Nova senha"
                          type={pwdShowNew ? 'text' : 'password'}
                          value={pwdNew}
                          onChange={(e) => setPwdNew(e.target.value)}
                          size="small"
                          fullWidth
                          autoComplete="new-password"
                          error={pwdNew.length > 0 && !pwdNewOk}
                          helperText={
                            pwdNew.length === 0
                              ? 'Mínimo 8 caracteres.'
                              : (!pwdNewOk ? `Faltam ${8 - pwdNew.length} caracteres.` : ' ')
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => setPwdShowNew(v => !v)}
                                  edge="end"
                                  aria-label={pwdShowNew ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                  {pwdShowNew ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          label="Confirmar nova senha"
                          type={pwdShowNew ? 'text' : 'password'}
                          value={pwdConfirm}
                          onChange={(e) => setPwdConfirm(e.target.value)}
                          size="small"
                          fullWidth
                          autoComplete="new-password"
                          error={pwdConfirm.length > 0 && !pwdConfirmOk}
                          helperText={
                            pwdConfirm.length === 0
                              ? 'Digite a nova senha de novo.'
                              : (!pwdConfirmOk ? 'As senhas não conferem.' : ' ')
                          }
                        />
                        {pwdCurrent && pwdNew && pwdCurrent === pwdNew && (
                          <Alert severity="warning" sx={{ borderRadius: 2 }} icon={<FiInfo size={16} />}>
                            A nova senha precisa ser diferente da atual.
                          </Alert>
                        )}
                      </Stack>
                      <Stack direction="row" justifyContent="flex-end" mt={3}>
                        <Button
                          onClick={handleChangePassword}
                          disabled={!pwdFormValid || pwdSaving}
                          variant="contained"
                          startIcon={pwdSaving
                            ? <CircularProgress size={14} color="inherit" />
                            : <FiSave size={14} />}
                          sx={{
                            bgcolor: '#25d366', color: '#0f172a', fontWeight: 800,
                            fontFamily: SYNE, textTransform: 'none', borderRadius: 2,
                            py: 1, px: 3,
                            boxShadow: '0 4px 15px rgba(37,211,102,0.3)',
                            '&:hover': { bgcolor: '#16a34a', color: '#fff' },
                            '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
                          }}
                        >
                          {pwdSaving ? 'Salvando...' : 'Alterar senha'}
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>

                  <ComingSoonBlock
                    icon={<FiShield size={22} color="#16a34a" />}
                    title="Sessões ativas em outros dispositivos"
                    body="Em breve você vai poder ver de quais dispositivos sua conta está logada e encerrar sessões remotamente."
                  />
                </Box>
              )}

              {/* ─── PREFERÊNCIAS ─── */}
              {tab === 2 && (
                <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                  <Paper elevation={0} sx={{
                    border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden', mb: 3,
                  }}>
                    <SectionHeader icon={<FiSliders size={16} color="#16a34a" />} title="Idioma da interface" />
                    <Box sx={{ p: 3 }}>
                      <TextField
                        select
                        label="Idioma"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        size="small"
                        sx={{ maxWidth: 320, width: '100%' }}
                      >
                        {LANGUAGES.map(l => (
                          <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
                        ))}
                      </TextField>
                      <Alert
                        severity="info"
                        icon={<FiInfo size={16} />}
                        sx={{ mt: 2, borderRadius: 2, fontSize: '0.82rem' }}
                      >
                        A interface ainda está toda em Português. Outros idiomas serão traduzidos numa próxima versão — sua preferência já fica salva.
                      </Alert>
                    </Box>
                  </Paper>

                  <Paper elevation={0} sx={{
                    border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden',
                  }}>
                    <SectionHeader icon={<FiSliders size={16} color="#16a34a" />} title="Notificações" />
                    <Box sx={{ p: 3 }}>
                      <ToggleRow
                        title="Novas mensagens"
                        description="Receber notificação sonora e visual quando um cliente responder."
                        checked={notifyMsgs}
                        onChange={setNotifyMsgs}
                      />
                      <Divider sx={{ my: 1.5, borderColor: '#f1f5f9' }} />
                      <ToggleRow
                        title="Alertas de Health Score"
                        description="Avisar quando um chip cair de saúde ou receber warning da Meta."
                        checked={notifyHealth}
                        onChange={setNotifyHealth}
                        badge="Em breve"
                      />
                    </Box>
                  </Paper>

                  <Stack direction="row" justifyContent="flex-end" mt={3}>
                    <Button
                      onClick={handleSavePrefs}
                      disabled={!prefsDirty || savingPrefs}
                      variant="contained"
                      startIcon={savingPrefs
                        ? <CircularProgress size={14} color="inherit" />
                        : <FiSave size={14} />}
                      sx={{
                        bgcolor: '#25d366', color: '#0f172a', fontWeight: 800,
                        fontFamily: SYNE, textTransform: 'none', borderRadius: 2,
                        py: 1, px: 3,
                        boxShadow: '0 4px 15px rgba(37,211,102,0.3)',
                        '&:hover': { bgcolor: '#16a34a', color: '#fff' },
                        '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
                      }}
                    >
                      {savingPrefs ? 'Salvando...' : 'Salvar preferências'}
                    </Button>
                  </Stack>
                </Box>
              )}

              {/* ─── PLANO E FATURAMENTO (em breve — PR 3) ─── */}
              {tab === 3 && isSupervisor && (
                <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                  <ComingSoonBlock
                    icon={<FiCreditCard size={22} color="#16a34a" />}
                    title="Plano atual e histórico de faturas"
                    body="Em breve seu plano atual, data de renovação e histórico de faturas aparecem aqui — hoje esses controles ficam em Administrativo → Assinatura e Upgrade."
                    action={
                      <Stack direction="row" spacing={1}>
                        <Button
                          onClick={() => navigate('/admin/assinatura')}
                          sx={{
                            textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
                            color: '#0f172a', borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
                          }}
                        >
                          Ver assinatura
                        </Button>
                        <Button
                          onClick={() => navigate('/admin/upgrade')}
                          variant="contained"
                          sx={{
                            bgcolor: '#25d366', color: '#0f172a', fontWeight: 800,
                            fontFamily: SYNE, textTransform: 'none', borderRadius: 2,
                            '&:hover': { bgcolor: '#16a34a', color: '#fff' },
                          }}
                        >
                          Fazer upgrade
                        </Button>
                      </Stack>
                    }
                  />
                </Box>
              )}
            </Paper>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Auxiliares ─────────────────────────────────────────────────────────
function SectionHeader({ icon, title, sub }) {
  return (
    <Box sx={{
      px: 3, py: 2.2,
      borderBottom: '1px solid #f1f5f9',
      display: 'flex', alignItems: 'center', gap: 1.5,
    }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: '10px',
        bgcolor: '#f0fdf4',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontFamily: SYNE, fontWeight: 800, color: '#0f172a', fontSize: '1rem' }}>
          {title}
        </Typography>
        {sub && (
          <Typography variant="caption" color="#94a3b8">{sub}</Typography>
        )}
      </Box>
    </Box>
  );
}

function ToggleRow({ title, description, checked, onChange, badge }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{
            fontFamily: SYNE, fontWeight: 700, color: '#0f172a', fontSize: '0.92rem',
          }}>
            {title}
          </Typography>
          {badge && (
            <Chip
              label={badge}
              size="small"
              sx={{
                bgcolor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a',
                fontFamily: SYNE, fontWeight: 700, fontSize: '0.62rem', height: 20,
              }}
            />
          )}
        </Stack>
        <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mt: 0.3 }}>
          {description}
        </Typography>
      </Box>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: '#25d366' },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#25d366' },
        }}
      />
    </Stack>
  );
}

function ComingSoonBlock({ icon, title, body, action }) {
  return (
    <Paper elevation={0} sx={{
      border: '1px dashed #cbd5e1', borderRadius: 3, p: 4,
      textAlign: 'center', bgcolor: '#f8fafc',
    }}>
      <Box sx={{
        width: 56, height: 56, borderRadius: '14px',
        bgcolor: '#f0fdf4', border: '1px solid #bbf7d0',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        mb: 2,
      }}>
        {icon}
      </Box>
      <Typography sx={{
        fontFamily: SYNE, fontWeight: 800, fontSize: '1.05rem', color: '#0f172a', mb: 1,
      }}>
        {title}
      </Typography>
      <Typography variant="body2" color="#64748b" sx={{ maxWidth: 480, mx: 'auto', mb: action ? 2.5 : 0 }}>
        {body}
      </Typography>
      {action}
    </Paper>
  );
}
